// Wrapper for making server requests
// Handles common errors / cases

'use strict';

const isObject = require('lodash/isObject');
const some = require('lodash/some');
const Promise = require('bluebird');

const Credentials = require('../local_credentials');
const Globals = require('../../../app/globals');

class PollTaskManager {
    constructor() {
        this.tasksInProgress = {};
    }

    getPollTaskFile(url) {
        return new Promise((resolve,reject)=>{
            $.ajax({
                url,
                type: 'GET',
                contentType: 'text/plain',
            })
            .done((data) => { 
                try { 
                    let result = JSON.parse(data);
                    resolve(result);
                }
                catch(err) {
                    reject(err);
                }
            })
            .fail((err) => {
                reject(err);
            })
        })
        
    }

    addPollTask(hash,actionID,resolve,reject, anonymous) {
        const keepAlive = 5000; // how often we should request task results
        var disableAutoLogout = false; // default is log out if they are unauthorized 
        var self = this;
        let url = anonymous ? `/authoring/poll_task_result_anon/${hash}` : `/authoring/poll_task_result/${hash}`;
        function waitForResult() {
            self.tasksInProgress[hash] = {actionID, anonymous};
            //console.log(`requesting results for poll task ${hash}`);
            $.ajax({
                url,
                type: 'GET',
                contentType: 'application/json',
            })
            .done((data) => { 
                // data = { status,  error,  result }
                if (data.status === 'pending') {
                    //console.log(`results still pending for poll task ${hash}`);
                    self.tasksInProgress[hash].keepAliveTimer = setTimeout(waitForResult,keepAlive); // recursive timer
                    return;
                }
                
                if (data.result) {
                    // console.log(`results obtained for poll task ${hash}`);
                    if (data.fileResult) {
                        // data.result is a filename.  request the file (which will be a stream)
                        let arg = encodeURIComponent(data.result);
                        let file_url = anonymous ? `/authoring/poll_task_file_result_anon/${hash}` : `/authoring/poll_task_file_result/${hash}`;
                        self.getPollTaskFile(file_url)
                        .then((fileData)=>{
                            delete self.tasksInProgress[hash]; // done with this task
                            resolve(fileData);
                        })
                        .catch((err)=>{
                            delete self.tasksInProgress[hash]; // done with this task
                            reject(err);
                        })
                    }
                    else {
                        delete self.tasksInProgress[hash]; // done with this task
                        resolve(data.result);
                    }
                }
                else if (data.error) {
                    // console.log(`error for poll task ${hash}`); 
                    delete self.tasksInProgress[hash]; // done with this task
                    reject(data.error);
                }
                else { // something wrong with the code
                    console.log(`bad poll task result for poll task ${hash}`, data);
                    reject("Bad poll task result");
                }
            })
            .fail((err) => {
                console.log(`error getting result for poll task ${hash}`, err);
                if ((err.status == 401) && !disableAutoLogout) {
                    self._logUserOut(self._getResLocationHeader(err));
                }
                if (self.tasksInProgress[hash]) {
                    clearTimeout(self.tasksInProgress[hash].keepAliveTimer);
                }
                delete self.tasksInProgress[hash];
                reject(err);
            });
        }
        
        waitForResult(); // start polling
    }
    
    _doAbandonTask(hash, anonymous) {
        let url = anonymous ? `/authoring/poll_task_result_anon/${hash}` : `/authoring/poll_task_result/${hash}`;
        $.ajax({
            url,
            type: 'DELETE',
            contentType: 'application/json',
        })
        .done((res) => { 
            // console.log(`abandoned poll task ${hash}`,res);
        })
        .fail((err) => {
            console.log(`failed to abandon poll task ${hash}`,err);
        });
        
    }
    
    cancelAction(actionID) {
        var found = false;
        Object.keys(this.tasksInProgress).every((hash) => {
            let entry = this.tasksInProgress[hash];
            if (entry && entry.actionID == actionID) {
                if (entry.keepAliveTimer) {
                    clearTimeout(entry.keepAliveTimer);
                    entry.keepAliveTimer = null;
                }
                this._doAbandonTask(hash,entry.anonymous);
                found = true;
                delete this.tasksInProgress[hash];
                return false;
            }
            return true;
        });
        if (!found) {
            console.log(`did not find poll task for actionID ${actionID}`); // FIXME: remove debug spam
        }
        return found;
    }
    
    abandonTasks() { // call this when a PollTaskManager goes away 
        Object.keys(this.tasksInProgress).forEach((hash) => {
            let entry = this.tasksInProgress[hash];
            if (entry && entry.keepAliveTimer) {
                // console.log(`cancelling keep alive timer for poll task ${hash}`);
                clearTimeout(entry.keepAliveTimer);
                delete entry.keepAliveTimer;
            }
            this._doAbandonTask(hash, entry.anonymous);
        });
        this.tasksInProgress = {};
    }
}

const api = {
    makeRequest: function(url, type, data, disableAutoLogout) {
        const self = this;

        return new Promise((resolve, reject) => {
            $.ajax({
                url: url,
                type: type,
                data: data || null,
                contentType: 'application/json',
            })
            .done((res) => {
                resolve(res);
            })
            .fail((err) => {
                if ((err.status == 401) && !disableAutoLogout) {
                    self._logUserOut(self._getResLocationHeader(err));
                }

                reject(err);
            });
        });
    },
    
    makeTaskRequest: function(url, type, data, disableAutoLogout) {
        const self = this;
        return new Promise((resolve, reject) => {
            $.ajax({
                url: url,
                type: type,
                data: data || null,
                contentType: 'application/json',
            })
            .done((res) => {
                // look for an error in the result in case we encountered an error after setting the header
                var obj = JSON.parse(res);
                if (obj.error) {
                    var err = obj.error;
                    if ((err.status == 401) && !disableAutoLogout) {
                        self._logUserOut(err.redirectUri);
                    }
                    reject(obj.error);
                }
                else {
                    resolve(obj.result);
                }
            })
            .fail((err) => {
                if ((err.status == 401) && !disableAutoLogout) {
                    self._logUserOut(self._getResLocationHeader(err));
                }
                reject(err);
            });
        });
    },
    
    PollTaskManager: PollTaskManager,

    makePollTaskRequest: function(url, type, data, manager, actionId, disableAutoLogout, anonymous) {
        if (!manager) {
            throw new Error("missing manager (PollTaskManager) ");
        }
        const self = this;
        return new Promise((resolve, reject) => {
            $.ajax({
                url: url,
                type: type,
                data: data || null,
                contentType: 'application/json',
            })
            .done((task_request_token) => {
                // task_request_token may either be a guid or stringified json object
                try {
                    const potentialResponse = JSON.parse(task_request_token); // will throw if task_request_token is a guid
                    if (!!potentialResponse.result) {
                        resolve(potentialResponse.result);
                    }
                    else {
                        reject("Parsed response contains no result");
                    }
                }
                catch(e) {
                    manager.addPollTask(task_request_token, actionId, resolve, reject, anonymous);
                }
            })
            .fail((err) => {
                if ((err.status == 401) && !disableAutoLogout) {
                    self._logUserOut(self._getResLocationHeader(err));
                }
                reject(err);
            });
        });
    },
    
    //includes a single asset from the file param as part of a server request
    makeUploadRequest: function(url, type, body, files, validator) {
        const self = this;

        return new Promise((resolve, reject) => {
            if (!isObject(body)) {
                reject("makeUploadRequest body must be of type Object. Received:", typeof body);
            }

            const file = files[0];

            if (!!validator && !validator(file)) {
                reject("The chosen file file is not of the correct type");
            }

            const formData = new FormData();
            formData.append(body.assetRole, file, file.name);
            formData.append('asset_name', file.name);

            Object.keys(body).forEach((key) => {
                formData.append(key, body[key]);
            });

            const xhr = new XMLHttpRequest();
            xhr.withCredentials = true;
            xhr.open(type, url, true);

            xhr.onload = (e) => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        let parsedResponse;
                        try {
                            parsedResponse = JSON.parse(xhr.responseText);
                        }
                        catch(e) {
                            // response can be a string. not necessarily an error.
                            console.warn('makeUploadRequest: Unable to parse response.');
                            parsedResponse = xhr.responseText;
                        }

                        resolve(parsedResponse);
                    }
                    else {
                        const genericErr = `Received non-200 status code: ${xhr.status}`;
                        const errText = xhr.responseText || genericErr;
                        reject(new Error(errText));
                    }
                }
            };

            xhr.onerror = (e) => {
                console.log('xhr.onerror');
                if (xhr.status === 401) {
                    self._logUserOut(self._getResLocationHeader(xhr));
                }

                console.log("upload error:");
                console.log(xhr);
                console.error(e);
                //"A network error occurred while uploading the asset"
                reject(e);
            };

            xhr.onabort = (e) => {
                console.log('xhr.onabort');
                console.log("upload aborted:");
                console.log(xhr);
                console.error(e);
                //"Asset upload aborted"
                reject(e);
            };

            xhr.send(formData);
        });
    },

    _getResLocationHeader: function(err) {
        if (
            !!err
            && typeof err.getResponseHeader === 'function'
        ) {
            return err.getResponseHeader('Location');
        }

        return null;
    },

    _logUserOut: function(redirectUri) {
        Credentials.clearLocalCredentials();
        const SignInStore = require('../stores/SignInStore'); // inline require to avoid circular dep issue
        const userInfo = SignInStore.getState().userInfo;
        const idpInfo = !!userInfo && !!userInfo.org_idp ? userInfo.org_idp : null;
        let idpLoginRedirect;
        if (!!idpInfo) {
            idpLoginRedirect = `/authoring/${idpInfo.type}/login/${idpInfo.token}`;
        }

        SignInStore.resetState();
        const currentPath = window.location.pathname;
        // do not redirect again if already at a sign-in path
        if (
            currentPath
            && (
                currentPath === '/signin'
                || some(Globals.idp_types, idpType => currentPath.indexOf(`/${idpType}`) >= 0)
            ) 
        ) {
            return;
        }

        if (idpLoginRedirect) {
            window.location.href = idpLoginRedirect;
        }
        else if (redirectUri) {
            window.location.href = redirectUri;
        }
        else {
            let queryString = '';
            if (currentPath) {
                queryString = `?${Globals.uri.query.REDIRECT_TO_PATH}=${currentPath}`;
            }

            window.location.href = `${GlobalConfig.ApiUrl}/signin${queryString}`;
        }
        
    },
    
};

module.exports = api;